import type { OrderedMaterialLine } from "@/lib/projectOverview";
import { fmtOrderDate } from "@/pages/bestellungen/orderHistoryDisplay";

/** Lieferstand einer Bestellkarte in der Projektübersicht. */
export type OrderCardDelivery = "open" | "delivered" | "partial" | "cancelled";

export interface OrderCardFacts {
  delivery: OrderCardDelivery;
  /** Anzeigetext fürs Badge, z. B. „Geliefert · 15.03.2026“ oder „Offen“. */
  statusLabel: string | null;
  /** Lieferant, Datum, Kürzel und Lieferort in einer Zeile. */
  meta: string | null;
}

function text(value: unknown): string | null {
  const raw = String(value ?? "").trim();
  return raw || null;
}

function germanDate(value: string | null): string | null {
  if (!value) return null;
  const formatted = fmtOrderDate(value);
  return formatted === "—" ? null : formatted;
}

function deliveryPlace(line: OrderedMaterialLine | undefined): string | null {
  if (!line) return null;
  const art = String(line.lieferart || "").trim().toLowerCase();
  const place = text(line.lieferadresse_abkuerzung);
  if (art === "abholung") return "Abholung";
  if (art === "lieferung") return place ? `Lieferung ${place}` : "Lieferung";
  return place;
}

/** Status und Kopfzeile für die gruppierten Zeilen einer Bestellnummer. */
export function orderCardFacts(lines: OrderedMaterialLine[]): OrderCardFacts {
  const active = lines.filter((line) => !line.order_cancelled);
  const delivered = active.filter((line) => Boolean(text(line.geliefert_at)));

  let delivery: OrderCardDelivery = "open";
  if (active.length === 0 && lines.some((line) => line.order_cancelled)) {
    delivery = "cancelled";
  } else if (active.length > 0 && delivered.length === active.length) {
    delivery = "delivered";
  } else if (delivered.length > 0) {
    delivery = "partial";
  }

  const deliveredOn =
    delivery === "delivered" ? germanDate(text(delivered[0]?.geliefert_at)) : null;

  let statusLabel: string | null = null;
  if (delivery === "delivered") {
    statusLabel = deliveredOn ? `Geliefert · ${deliveredOn}` : "Geliefert";
  } else if (delivery === "partial") {
    statusLabel = "Teilweise geliefert";
  } else if (delivery === "open") {
    statusLabel = "Offen";
  }

  const sample =
    lines.find((line) => text(line.supplier_name) || text(line.order_created_at)) ?? lines[0];
  const orderedOn = germanDate(text(sample?.order_created_at));
  const meta =
    [
      text(sample?.supplier_name),
      text(sample?.supplier_contact_name),
      orderedOn ? `Bestellt ${orderedOn}` : null,
      text(sample?.kuerzel),
      deliveryPlace(sample),
    ]
      .filter(Boolean)
      .join(" · ") || null;

  return { delivery, statusLabel, meta };
}
